import APP_CONFIG from '../config/appConfig';
import errorTrackingService from './errorTrackingService';

const API_BASE_URL = APP_CONFIG.API.BASE_URL;

/**
 * BookmarkAPI Service for communicating with Spring Boot backend
 */
class BookmarkAPI {
  constructor() {
    this.baseURL = `${API_BASE_URL}/bookmarks`;
  }

  /**
   * Make HTTP request with error handling
   */
  async makeRequest(url, options = {}) {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      ...options,
    };

    // 🔒 SECURITY FIX: Strict authentication required
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please log in first to access bookmarks.');
    }
    config.headers.Authorization = `Bearer ${token}`;

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Track HTTP errors with detailed context
        // Only track errors if not in development mode  
        if (process.env.NODE_ENV !== 'development') {
          errorTrackingService.trackCustomError(
            'HTTP_ERROR',
            `BookmarkAPI failed: HTTP ${response.status} - ${url}`,
            {
              method: config.method || 'GET',
              endpoint: url,
              httpStatus: response.status,
              component: 'BookmarkAPI',
              errorData: errorData
            }
          );
        } else {
          console.warn('🚨 Development: BookmarkAPI HTTP Error:', {
            status: response.status,
            url: url,
            method: config.method || 'GET',
            requestBody: config.body,
            errorData: errorData,
            headers: config.headers
          });
        }
        
        throw new Error(errorData.message || `HTTP Error: ${response.status}`);
      }

      // Handle empty responses (like DELETE)
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      
      // Track network/connection errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        // Only track errors if not in development mode
        if (process.env.NODE_ENV !== 'development') {
          errorTrackingService.trackCustomError(
            'NETWORK_ERROR',
            `BookmarkAPI network error: ${url}`,
            {
              method: config.method || 'GET',
              endpoint: url,
              component: 'BookmarkAPI',
              originalError: error.message
            }
          );
        } else {
          console.warn('🚨 Development: BookmarkAPI Network Error:', url, error.message);
        }
      }
      
      throw error;
    }
  }

  /**
   * Get all bookmarks with optional sorting
   */
  async getAllBookmarks(sortBy = 'newest') {
    const url = `${this.baseURL}?sort=${sortBy}`;
    return await this.makeRequest(url);
  }

  /**
   * Get bookmark by ID
   */
  async getBookmarkById(id) {
    const url = `${this.baseURL}/${id}`;
    return await this.makeRequest(url);
  }

  /**
   * Create new bookmark
   */
  async createBookmark(bookmarkData) {
    const url = this.baseURL;
    return await this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify(bookmarkData),
    });
  }

  /**
   * Update existing bookmark
   */
  async updateBookmark(id, bookmarkData) {
    const url = `${this.baseURL}/${id}`;
    return await this.makeRequest(url, {
      method: 'PUT',
      body: JSON.stringify(bookmarkData),
    });
  }

  /**
   * Delete bookmark
   */
  async deleteBookmark(id) {
    const url = `${this.baseURL}/${id}`;
    return await this.makeRequest(url, {
      method: 'DELETE',
    });
  }

  /**
   * Delete multiple bookmarks
   */
  async deleteMultipleBookmarks(bookmarkIds) {
    const url = `${this.baseURL}/batch`;
    return await this.makeRequest(url, {
      method: 'DELETE',
      body: JSON.stringify(bookmarkIds),
    });
  }

  /**
   * Meldet dem Backend den ersten Aufruf eines Lesezeichens (fire-and-forget).
   * Löst serverseitig beim allerersten Aufruf einen Plain-Text-Content-Snapshot
   * der Zielseite aus (siehe TODO_Archiv_Backend.md Abschnitt 5.1) - idempotent,
   * spätere Aufrufe sind No-Ops. Antwortet laut Spezifikation immer mit 200,
   * daher wird hier bewusst kein Fehler nach oben geworfen: Ein Fehlschlag darf
   * das eigentliche Öffnen des Lesezeichens nicht beeinträchtigen.
   */
  async visitBookmark(id) {
    try {
      await this.makeRequest(`${this.baseURL}/${id}/visit`, { method: 'POST' });
    } catch (error) {
      console.warn('[Bookmarks] visit-Call fehlgeschlagen (unkritisch):', error.message);
    }
  }

  /**
   * Search bookmarks
   */
  async searchBookmarks(searchTerm) {
    const url = `${this.baseURL}/search${searchTerm ? `?q=${encodeURIComponent(searchTerm)}` : ''}`;
    return await this.makeRequest(url);
  }

  /**
   * Get bookmark statistics
   */
  async getBookmarkStats() {
    const url = `${this.baseURL}/stats`;
    return await this.makeRequest(url);
  }

  /**
   * Check if API is available
   */
  async checkHealth() {
    try {
      const response = await fetch(`${API_BASE_URL}/actuator/health`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

const bookmarkApiInstance = new BookmarkAPI();
export default bookmarkApiInstance;